'use client';

import { useState } from 'react';
import {
  createContext,
  useContext,
  useContextSelector,
} from 'use-context-selector';

export const SetActiveAssetIdContext = createContext<
  (assetId: string | undefined) => void
>(() => undefined);
export const IsolatedActiveAssetIdContext = createContext<string | undefined>(
  undefined
);

export const useActiveAssetId = () => useContext(IsolatedActiveAssetIdContext);
export const useSetActiveAssetId = () => useContext(SetActiveAssetIdContext);

type IsolatedActiveAssetContextProviderProps = {
  children: React.ReactNode;
  assetId: string;
  /**
   * If you want the assetId from the prop to always be the source of truth
   * when it changes from existing to null, then this should be set to true.
   *
   * If you only want truthy assetId props to be the source of truth, then
   * this should be set to false.
   */
  shouldAcceptNullishProps?: boolean;
};

export const IsolatedActiveAssetIdContextProvider = ({
  assetId: assetIdFromProps,
  shouldAcceptNullishProps = true,
  children,
}: IsolatedActiveAssetContextProviderProps) => {
  const [prevAssetIdFromProps, setPrevAssetIdFromProps] = useState<
    string | undefined
  >(assetIdFromProps);
  const [assetId, setAssetId] = useState<string | undefined>(assetIdFromProps);

  // Track every prop change, including ones we don't apply. If a rejected
  // nullish prop left `prev` pointing at the last truthy value, that value
  // arriving again would look unchanged and never override an imperative update.
  if (prevAssetIdFromProps !== assetIdFromProps) {
    setPrevAssetIdFromProps(assetIdFromProps);
    if (shouldAcceptNullishProps || !!assetIdFromProps) {
      setAssetId(assetIdFromProps);
    }
  }

  return (
    <IsolatedActiveAssetIdContext.Provider value={assetId}>
      <SetActiveAssetIdContext.Provider value={setAssetId}>
        {children}
      </SetActiveAssetIdContext.Provider>
    </IsolatedActiveAssetIdContext.Provider>
  );
};

export const useIsAssetActive = (assetId: string) =>
  useContextSelector(
    IsolatedActiveAssetIdContext,
    (activeAssetId) => activeAssetId === assetId
  );
